import { createRoute, redirect } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useSession } from "@/frontend/auth-client";
import { Route as patientLayout } from "./patient-layout";
import { PageShell } from "@/frontend/components/dashboard/PageShell";
import { PatientPageHeader } from "@/frontend/components/dashboard/patient/PatientPageHeader";
import { OnboardingTabs } from "@/frontend/components/dashboard/patient/OnboardingTabs";
import { DEFAULT_ONBOARDING_TAB } from "@/frontend/types/dashboard/nav";
import { isOnboardingTabId } from "@/frontend/utils/dashboard/nav";
import { ConnectHealthSystems } from "@/frontend/components/dashboard/individual/ConnectHealthSystems";
import { useWorkspacePersona } from "@/frontend/components/dashboard/individual/useWorkspacePersona";
import {
  useActiveMemberQuery,
  usePatientSummaryQuery,
} from "@/frontend/queries/dashboard/patients";
import { patientFirstName } from "@/frontend/utils/patient-name";

/**
 * Bare `/onboarding` has no tab in it, so it cannot be linked, bookmarked or
 * reloaded back onto the tab someone was reading. It redirects to the default
 * tab instead of rendering, which keeps one URL per tab. Redirecting
 * unconditionally also means the persona never has to be known before routing.
 */
export const Route = createRoute({
  getParentRoute: () => patientLayout,
  path: "/onboarding",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/dashboard/patients/$patientId/onboarding/$tab",
      params: { patientId: params.patientId, tab: DEFAULT_ONBOARDING_TAB },
      replace: true,
    });
  },
});

/** One Onboarding tab, named in the path so it survives a reload or a shared link. */
export const TabRoute = createRoute({
  getParentRoute: () => patientLayout,
  path: "/onboarding/$tab",
  beforeLoad: ({ params }) => {
    if (isOnboardingTabId(params.tab)) return;
    throw redirect({
      to: "/dashboard/patients/$patientId/onboarding/$tab",
      params: { patientId: params.patientId, tab: DEFAULT_ONBOARDING_TAB },
      replace: true,
    });
  },
  component: PatientOnboardingPage,
});

/**
 * Setup and onboarding for one patient. A workspace that onboards other people
 * gets two tabs, Setup and Connected systems, each with its own URL. In an
 * individual workspace the owner is the patient, so there are no tabs and the
 * page becomes their own connect surface with the tab segment ignored.
 */
function PatientOnboardingPage() {
  const { patientId, tab } = TabRoute.useParams();
  const { data: session } = useSession();
  const isPlatformAdmin = session?.user?.role === "admin";
  const { isIndividual, isLoading: isPersonaLoading, organizationId, organizationName } =
    useWorkspacePersona();

  const activeMemberQuery = useActiveMemberQuery(!!session && !isPlatformAdmin);
  const roles = (activeMemberQuery.data?.role ?? "")
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
  const canManage =
    isPlatformAdmin || roles.some((role) => ["admin", "owner", "member"].includes(role));

  const { data: summary, isLoading } = usePatientSummaryQuery(patientId);
  const given = patientFirstName(summary?.row.name, summary?.externalId);

  if (isLoading || !summary || isPersonaLoading) {
    return (
      <PageFrame patientId={patientId}>
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      </PageFrame>
    );
  }

  if (isIndividual) {
    return (
      <PageFrame patientId={patientId}>
        <div className="min-h-0 flex-1 lg:overflow-y-auto">
          <PageShell className="py-6">
            <ConnectHealthSystems
              patientMembershipId={patientId}
              organizationId={organizationId}
              organizationName={organizationName}
            />
          </PageShell>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame patientId={patientId}>
      <OnboardingTabs
        patientMembershipId={patientId}
        patientFirstName={given}
        patientFullName={summary.row.name}
        askConsentSignature={summary.row.askConsentSignature}
        showMobileApp={summary.row.showMobileApp}
        canManage={canManage}
        activeTab={isOnboardingTabId(tab) ? tab : DEFAULT_ONBOARDING_TAB}
      />
    </PageFrame>
  );
}

/**
 * One viewport-tall frame under the patient header, so the tab bar and the phone
 * preview inside it can claim the height the window has left rather than a
 * height worked out from the chrome above them.
 */
function PageFrame({
  patientId,
  children,
}: {
  patientId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-svh flex-col lg:h-svh lg:min-h-0 lg:overflow-hidden">
      <PatientPageHeader patientId={patientId} />
      {children}
    </div>
  );
}
